import * as React from "react";
import { useState, useRef, useEffect } from "react";
import { Avatar, Divider, Badge, Tabs, Tab, Box, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Button } from "@mui/material";
import NotificationsNoneIcon from "@mui/icons-material/NotificationsNone";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import { useNavigate } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import NotificationEmailFirstname from "../layout/NotificationEmailFirstname";
import { useNotifications } from "../../hooks/useNotifications";

// ─── helpers ─────────────────────────────────────────────────────────────────

function timeAgo(timestamp) {
  const diff = Date.now() - new Date(timestamp).getTime();
  const s = Math.floor(diff / 1000);
  const m = Math.floor(s / 60);
  const h = Math.floor(m / 60);
  const d = Math.floor(h / 24);
  if (d > 30) return `${Math.floor(d / 30)}mo ago`;
  if (d > 0) return `${d}d ago`;
  if (h > 0) return `${h}h ago`;
  if (m > 0) return `${m}m ago`;
  return "just now";
}

function initials(name = "") {
  const parts = name.trim().split(/\s+/);
  return parts.length >= 2
    ? (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
    : name.slice(0, 2).toUpperCase();
}

function displayName(authorName = "") {
  if (!authorName) return "Unknown";
  if (authorName.includes("@")) {
    const local = authorName.split("@")[0];
    return local
      .split(".")
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
      .join(" ");
  }
  return authorName;
}

// ─── single notification card ─────────────────────────────────────────────────

function NotificationCard({ noti, onMarkRead, onReply, onView }) {
  const unread = noti.status === 1;
  const name = displayName(noti.authorName);
  const ini = initials(name);

  return (
    <div
      className={`relative flex gap-3 px-4 py-3 cursor-pointer transition-colors hover:bg-gray-50 ${
        unread ? "bg-blue-50/40" : "bg-white"
      }`}
      onClick={() => onMarkRead(noti.notificationId)}
    >
      {/* unread dot */}
      {unread && (
        <span className="absolute top-3 right-3 w-2 h-2 rounded-full bg-blue-500" />
      )}

      {/* avatar */}
      <div className="relative flex-shrink-0" style={{ width: 38, height: 38 }}>
        <Avatar
          sx={{
            width: 38,
            height: 38,
            bgcolor: "#253978",
            fontSize: 14,
            fontWeight: 700,
          }}
        >
          {ini}
        </Avatar>
        <span
          style={{
            position: "absolute",
            bottom: -3,
            right: -3,
            width: 16,
            height: 16,
            borderRadius: "50%",
            background: "#d82d2d",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            border: "2px solid white",
          }}
        >
          <ChatBubbleOutlineIcon sx={{ fontSize: 8, color: "white" }} />
        </span>
      </div>

      {/* content */}
      <div className="flex-1 min-w-0">
        {/* top row: name + time */}
        <div className="flex items-center justify-between gap-2 mb-0.5">
          <span className="text-sm font-semibold text-gray-800 truncate">
            {name}
            <span className="text-gray-500 font-normal"> commented</span>
          </span>
          <span className="text-xs text-gray-400 flex-shrink-0">
            {timeAgo(noti.timestamp)}
          </span>
        </div>

        {/* message preview */}
        <div className="text-sm text-gray-600 line-clamp-2 mb-1.5">
          <NotificationEmailFirstname text={noti.message} />
        </div>

        {/* chips */}
        <div className="flex flex-wrap gap-1 mb-2">
          {noti.reportName && (
            <span className="text-xs px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-700 font-medium">
              {noti.reportName}
            </span>
          )}
          {noti.groupName && (
            <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">
              {noti.groupName}
            </span>
          )}
        </div>

        {/* actions */}
        <div className="flex gap-3 text-xs font-medium">
          <button
            className="text-blue-600 hover:text-blue-800 hover:underline"
            onClick={(e) => {
              e.stopPropagation();
              onReply(noti);
            }}
          >
            Reply
          </button>
          <button
            className="text-blue-600 hover:text-blue-800 hover:underline"
            onClick={(e) => {
              e.stopPropagation();
              onView(noti);
            }}
          >
            View
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── empty state ──────────────────────────────────────────────────────────────

function EmptyState({ label }) {
  return (
    <div className="flex flex-col items-center justify-center py-14 px-6 text-center">
      <NotificationsNoneIcon sx={{ fontSize: 48, color: "#d1d5db", mb: 1 }} />
      <p className="text-gray-400 font-medium text-sm">{label}</p>
    </div>
  );
}

// ─── main component ───────────────────────────────────────────────────────────

export default function Notifications() {
  const {
    fetchNotifications: fetchNotificationsApi,
    markNotificationRead,
    markAllNotificationsRead,
    createComment,
    switchDefaultUserGroup,
  } = useNotifications();
  const [openNoti, setOpenNoti] = useState(false);
  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);

  // reply dialog
  const [replyOpen, setReplyOpen] = useState(false);
  const [replyTarget, setReplyTarget] = useState(null);
  const [replyText, setReplyText] = useState("");
  const [replying, setReplying] = useState(false);

  // switch persona dialog
  const [switchOpen, setSwitchOpen] = useState(false);
  const [switchMeta, setSwitchMeta] = useState({});
  const [switching, setSwitching] = useState(false);

  const divRef = useRef(null);
  const navigate = useNavigate();

  // close on outside click
  useEffect(() => {
    const handler = (e) => {
      if (divRef.current && !divRef.current.contains(e.target) && !replyOpen && !switchOpen) {
        setOpenNoti(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [replyOpen, switchOpen]);

  // ── API calls ──────────────────────────────────────────────────────────────

  const fetchNotifications = async () => {
    try {
      const json = await fetchNotificationsApi();
      const msgs = (json.data?.messages || []).filter(Boolean);
      setNotifications(msgs);
      setUnreadCount(json.data?.unreadCount || 0);
    } catch {
      setNotifications([]);
      setUnreadCount(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchNotifications();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const markRead = (id) => {
    markNotificationRead(id)
      .then(() => fetchNotifications())
      .catch(console.error);
  };

  const markAllRead = () => {
    markAllNotificationsRead()
      .then(() => fetchNotifications())
      .catch(console.error);
  };

  const handleBellClick = () => {
    setOpenNoti((v) => !v);
    if (!openNoti) fetchNotifications();
  };

  // ── reply ──────────────────────────────────────────────────────────────────

  const openReply = (noti) => {
    setReplyTarget(noti);
    setReplyText("");
    setReplyOpen(true);
  };

  const submitReply = () => {
    if (!replyText.trim()) return;
    setReplying(true);
    createComment({
      topicIdentifier: replyTarget.topicIdentifier,
      email: localStorage.getItem("myemail"),
      subject: replyText,
      parentCommentId: replyTarget.commentId,
      topicId: replyTarget.postId,
    })
      .then(() => {
        toast.success(`Replied to ${displayName(replyTarget.authorName)}`, {
          position: "bottom-right",
          autoClose: 4000,
        });
        setReplyOpen(false);
        setReplyText("");
      })
      .catch(() => toast.error("Failed to send reply"))
      .finally(() => setReplying(false));
  };

  // ── view / switch persona ─────────────────────────────────────────────────

  const handleView = (noti) => {
    if (noti.currentId === noti.commentGroupId) {
      const path =
        noti.reportId === 118
          ? `/LIMS/${noti.reportId}?notificationiD=${noti.postId}`
          : noti.reportId === 113
          ? `/Labs/${noti.reportId}?notificationiD=${noti.postId}`
          : `/reports/${noti.reportId}/view?notificationiD=${noti.postId}`;
      navigate(path);
    } else {
      setSwitchMeta(noti);
      setSwitchOpen(true);
    }
  };

  const switchPersona = () => {
    setSwitching(true);
    switchDefaultUserGroup(localStorage.getItem("myemail"), switchMeta.commentGroupId)
      .then(() => {
        const id = switchMeta.reportId;
        const cid = switchMeta.postId;
        navigate(
          id === 118
            ? `/LIMS/${id}?notificationiD=${cid}`
            : id === 113
            ? `/Labs/${id}?notificationiD=${cid}`
            : `/reports/${id}/view?notificationiD=${cid}`
        );
        window.location.reload();
      })
      .catch(console.error)
      .finally(() => setSwitching(false));
  };

  // ── render ─────────────────────────────────────────────────────────────────

  const unread = notifications.filter((n) => n.status === 1);
  const displayed = tab === 0 ? notifications : unread;

  return (
    <div id="NotificationDrawer" ref={divRef} className="relative">
      {/* bell button */}
      <button
        onClick={handleBellClick}
        className="relative flex items-center justify-center w-9 h-9 rounded-full hover:bg-gray-100 transition-colors"
      >
        <Badge badgeContent={unreadCount} max={99} color="error">
          <NotificationsNoneIcon sx={{ fontSize: 22, color: "#374151" }} />
        </Badge>
      </button>

      {/* dropdown panel */}
      {openNoti && (
        <div
          className="absolute right-0 mt-2 w-[500px] bg-white rounded-xl shadow-2xl border border-gray-100 z-50 overflow-hidden"
          style={{ maxHeight: "520px", display: "flex", flexDirection: "column" }}
        >
          {/* header */}
          <div className="flex items-center justify-between px-4 pt-4 pb-2">
            <span className="text-base font-bold text-gray-800">Notifications</span>
            <div className="flex items-center gap-2">
              {unreadCount > 0 && (
                <button
                  onClick={markAllRead}
                  className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 font-medium"
                >
                  <CheckCircleOutlineIcon sx={{ fontSize: 14 }} />
                  Mark all read
                </button>
              )}
              <button
                onClick={() => setOpenNoti(false)}
                className="text-gray-400 hover:text-gray-600 p-1 rounded-full hover:bg-gray-100 transition-colors"
              >
                <CloseIcon sx={{ fontSize: 16 }} />
              </button>
            </div>
          </div>

          <Divider />

          {/* tabs */}
          <Box sx={{ borderBottom: 1, borderColor: "divider", px: 1 }}>
            <Tabs
              value={tab}
              onChange={(_, v) => setTab(v)}
              sx={{ minHeight: 36 }}
              TabIndicatorProps={{ style: { height: 2 } }}
            >
              <Tab
                label="All"
                sx={{ fontSize: 13, minHeight: 36, textTransform: "none", fontWeight: 600 }}
              />
              <Tab
                label={`Unread${unreadCount > 0 ? ` (${unreadCount})` : ""}`}
                sx={{ fontSize: 13, minHeight: 36, textTransform: "none", fontWeight: 600 }}
              />
            </Tabs>
          </Box>

          {/* list */}
          <div className="overflow-y-auto flex-1">
            {loading ? (
              <div className="flex flex-col gap-3 p-4">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="flex gap-3 animate-pulse">
                    <div className="w-10 h-10 rounded-full bg-gray-200 flex-shrink-0" />
                    <div className="flex-1 space-y-2">
                      <div className="h-3 bg-gray-200 rounded w-3/4" />
                      <div className="h-3 bg-gray-200 rounded w-full" />
                      <div className="h-3 bg-gray-200 rounded w-1/2" />
                    </div>
                  </div>
                ))}
              </div>
            ) : displayed.length === 0 ? (
              <EmptyState
                label={tab === 0 ? "No notifications yet" : "You're all caught up!"}
              />
            ) : (
              displayed.map((noti, i) => (
                <React.Fragment key={noti.notificationId ?? i}>
                  <NotificationCard
                    noti={noti}
                    onMarkRead={markRead}
                    onReply={openReply}
                    onView={handleView}
                  />
                  {i < displayed.length - 1 && <Divider />}
                </React.Fragment>
              ))
            )}
          </div>
        </div>
      )}

      <ToastContainer />

      {/* ── reply dialog ── */}
      <Dialog open={replyOpen} onClose={() => setReplyOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ pb: 0 }}>
          <div className="flex items-center justify-between">
            <span className="text-base font-semibold">
              Reply to{" "}
              <span className="text-red-600">{displayName(replyTarget?.authorName)}</span>
            </span>
            <button
              onClick={() => setReplyOpen(false)}
              className="text-gray-400 hover:text-gray-600"
            >
              <CloseIcon sx={{ fontSize: 18 }} />
            </button>
          </div>
          <div className="flex gap-2 mt-1">
            {replyTarget?.reportName && (
              <span className="text-xs px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-700">
                {replyTarget.reportName}
              </span>
            )}
            {replyTarget?.groupName && (
              <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">
                {replyTarget.groupName}
              </span>
            )}
          </div>
        </DialogTitle>
        <DialogContent>
          <div className="bg-gray-50 rounded-lg p-3 text-sm text-gray-700 mb-3 border-l-4 border-indigo-300">
            {replyTarget?.message}
          </div>
          <textarea
            autoFocus
            className="w-full border border-gray-200 rounded-lg p-3 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-indigo-300"
            rows={4}
            placeholder="Write your reply…"
            value={replyText}
            onChange={(e) => setReplyText(e.target.value)}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setReplyOpen(false)}
            size="small"
            sx={{ textTransform: "none", color: "#6b7280" }}
          >
            Cancel
          </Button>
          <Button
            onClick={submitReply}
            disabled={replying || !replyText.trim()}
            size="small"
            variant="contained"
            sx={{ textTransform: "none", bgcolor: "#253978", "&:hover": { bgcolor: "#1e2f5e" } }}
          >
            {replying ? "Sending…" : "Send Reply"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── switch persona dialog ── */}
      <Dialog open={switchOpen} onClose={() => setSwitchOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontSize: 16, fontWeight: 700 }}>Switch Persona</DialogTitle>
        <DialogContent>
          <DialogContentText className="text-sm text-gray-600">
            This notification is from a different persona. Switch to{" "}
            <strong>{switchMeta.grname}</strong> to view it?
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setSwitchOpen(false)}
            size="small"
            sx={{ textTransform: "none", color: "#6b7280" }}
          >
            Cancel
          </Button>
          <Button
            onClick={switchPersona}
            disabled={switching}
            size="small"
            variant="contained"
            sx={{ textTransform: "none", bgcolor: "#253978", "&:hover": { bgcolor: "#1e2f5e" } }}
          >
            {switching ? "Switching…" : "Switch & View"}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
